import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { VatStatus, can } from '@jalupro/shared';
import { api, useApi, useMutation } from '../../api';
import { useAuth } from '../../auth';
import type { ClientView, UserView } from '../../api';
import { LoadingState, ErrorState, EmptyState, VatDot, SearchIcon } from '../../components';
import { useT, useLang } from '../../i18n';
import type { TFunc } from '../../i18n';
import dict from './Clients.i18n';
import { countryName, countryFlag } from '../../lib/countries';
import { MODULES } from '../../modules';

/** Map a VAT status to its localized tooltip key (validity dot hover). */
const VAT_LABEL_KEY: Record<VatStatus, string> = {
  [VatStatus.VALID]: 'vatValid',
  [VatStatus.INVALID]: 'vatInvalid',
  [VatStatus.UNAVAILABLE]: 'vatUnavailable',
  [VatStatus.UNVERIFIED]: 'vatUnverified',
  [VatStatus.NOT_APPLICABLE]: 'vatNotApplicable',
};

/** Compose the dot tooltip: localized status, plus the last-checked date when known. */
function vatTitle(c: ClientView, t: TFunc): string {
  const label = t(VAT_LABEL_KEY[c.vatStatus]);
  if (c.vatCheckedAt) {
    return `${label} · ${t('vatCheckedOn', {
      date: new Date(c.vatCheckedAt).toLocaleDateString(),
    })}`;
  }
  return label;
}

/**
 * Admin · Clients — the global client directory. Route /admin/clients
 * (ADMIN / SUPER_ADMIN, route-gated).
 *
 * Reads an optional `agentId` query param (set by the Agents directory's
 * client-count column) to scope the list to one agent's clients. Create and
 * view/edit navigate to dedicated routes (/admin/clients/new and
 * /admin/clients/:id/edit) so the client id is reflected in the URL (deep-linkable).
 *
 * Wired to the live /clients API:
 *   - GET    /clients?agentId&search&page&pageSize → api.clients.list
 *   - DELETE /clients/:id                          → api.clients.remove
 *     (hard-deletes when the client has no orders, else soft-archives).
 */

const PAGE_SIZE = 50;

function initials(name: string): string {
  return name
    .split(' ')
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

const PLACEHOLDER = '—';

/** Date of the client's most recent order (localized short date), or a dash. */
function lastOrderLabel(c: ClientView, lang: string): string {
  const iso = c.totals?.lastOrderAt;
  if (!iso) return PLACEHOLDER;
  return new Date(iso).toLocaleDateString(lang, { day: 'numeric', month: 'short' });
}

export default function AdminClients() {
  const t = useT(dict);
  const { lang } = useLang();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const agentId = searchParams.get('agentId') ?? undefined;

  const [search, setSearch] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  // Correction list: clients blocked for orders or missing a mandatory field.
  const [correctionOnly, setCorrectionOnly] = useState(false);

  const { data, loading, error, reload } = useApi(
    () =>
      api.clients.list({
        ...(agentId ? { agentId } : {}),
        ...(correctionOnly ? { correctionOnly: true } : {}),
        search: search.trim() || undefined,
        page: 1,
        pageSize: PAGE_SIZE,
      }),
    [agentId, search, correctionOnly],
  );

  // Agent roster for the owner filter dropdown.
  const { data: agentsData } = useApi(() => api.agents.list({ page: 1, pageSize: 100 }), []);
  const agents = (agentsData?.items ?? []) as UserView[];

  function setAgentFilter(value: string) {
    const next = new URLSearchParams(searchParams);
    if (value) next.set('agentId', value);
    else next.delete('agentId');
    setSearchParams(next);
  }

  const removeMutation = useMutation(api.clients.remove);
  // Deletion / anonymisation is Super Admin only (matrix: clients.deleteAnonymise).
  const { role } = useAuth();
  const canDelete = !!role && can(role, 'clients.deleteAnonymise');
  const canReassign = !!role && can(role, 'clients.reassign');

  const clients = data?.items ?? [];

  const subtitle = (() => {
    if (data) {
      return t(data.total === 1 ? 'subtitleCount_one' : 'subtitleCount_other', {
        count: data.total,
      });
    }
    return agentId ? t('subtitleFiltered') : t('subtitleAll');
  })();

  function onDelete(c: ClientView) {
    if (!window.confirm(t('deleteConfirm', { name: c.company }))) return;
    setNotice(null);
    removeMutation
      .mutate(c.id)
      .then((res) => {
        if (res.archived) setNotice(t('deleteArchivedNotice', { name: c.company }));
        return reload();
      })
      .catch(() => {
        /* removeMutation.error is surfaced below */
      });
  }

  return (
    <>
      {/* Topbar */}
      <div
        className="row center between"
        style={{ padding: '14px 22px', borderBottom: '1px solid var(--line)', background: '#fff' }}
      >
        <div className="col">
          <div className="meta">{subtitle}</div>
          <div className="h1">{t('heading')}</div>
        </div>
        <div className="row gap-8 center">
          <label className="row gap-6 center meta" style={{ fontSize: 13, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={correctionOnly}
              onChange={(e) => setCorrectionOnly(e.target.checked)}
            />
            {t('filterCorrection')}
          </label>
          <select
            className="input"
            value={agentId ?? ''}
            onChange={(e) => setAgentFilter(e.target.value)}
            aria-label={t('filterAgentLabel')}
            style={{ width: 200 }}
          >
            <option value="">{t('filterAllAgents')}</option>
            <option value="unassigned">{t('filterUnassigned')}</option>
            {agents.map((a) => (
              <option key={a.id} value={a.id}>
                {a.fullName}
              </option>
            ))}
          </select>
          <label className="input" style={{ width: 240 }}>
            <SearchIcon />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('searchPlaceholder')}
              aria-label={t('searchPlaceholder')}
              style={{
                border: 'none',
                outline: 'none',
                background: 'transparent',
                font: 'inherit',
                color: 'var(--ink)',
                width: '100%',
              }}
            />
          </label>
          {canReassign && (
            <button type="button" className="btn" onClick={() => navigate('/admin/clients/reassign')}>
              {t('reassignBtn')}
            </button>
          )}
          <button
            type="button"
            className="btn primary"
            onClick={() => navigate('/admin/clients/new')}
          >
            {t('newClientBtn')}
          </button>
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: '14px 22px', overflow: 'auto', flex: 1 }}>
        {removeMutation.error && (
          <div className="mb-12">
            <ErrorState message={removeMutation.error.message} />
          </div>
        )}
        {notice && (
          <div
            className="panel mb-12"
            style={{ padding: '10px 14px', background: 'var(--amber-bg)', border: '1px solid var(--amber-line)' }}
          >
            <span className="meta" style={{ fontSize: 13, color: 'var(--amber)' }}>
              {notice}
            </span>
          </div>
        )}

        <div className="panel">
          {loading ? (
            <LoadingState label={t('loadingClients')} />
          ) : error ? (
            <div style={{ padding: 14 }}>
              <ErrorState message={error.message} onRetry={reload} />
            </div>
          ) : clients.length === 0 ? (
            <EmptyState
              title={search ? t('noMatchTitle') : t('emptyTitle')}
              hint={search ? t('noMatchHint') : t('emptyHint')}
            />
          ) : (
            <div style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th></th>
                  <th>{t('colClient')}</th>
                  <th>{t('colStatus')}</th>
                  <th>{t('colCifVat')}</th>
                  <th>{t('colVat')}</th>
                  <th>{t('colCity')}</th>
                  <th>{t('colCountry')}</th>
                  <th>{t('colEmail')}</th>
                  <th>{t('colOwnerAgent')}</th>
                  {MODULES.orders && <th style={{ textAlign: 'right' }}>{t('colOrders')}</th>}
                  {MODULES.orders && <th>{t('colLastOrder')}</th>}
                  <th style={{ textAlign: 'right' }}>{t('colDeliveries')}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {clients.map((c, i) => (
                  <tr key={c.id}>
                    <td>
                      <div
                        style={{
                          width: 30,
                          height: 30,
                          background: i % 2 ? 'var(--line-2)' : 'var(--line)',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 12,
                          fontWeight: 600,
                          color: 'var(--muted)',
                        }}
                      >
                        {initials(c.company)}
                      </div>
                    </td>
                    <td style={{ fontWeight: 500 }}>{c.company}</td>
                    <td>
                      <span
                        className={`pill ${c.blockedForOrders ? 'red' : c.needsCorrection ? 'amber' : 'green'} dot`}
                        title={c.correctionReasons.join(' · ')}
                      >
                        {c.blockedForOrders ? t('statusBlocked') : c.needsCorrection ? t('statusCorrection') : t('statusOk')}
                      </span>
                    </td>
                    <td className="mono" style={{ fontSize: 12, color: 'var(--muted)' }}>
                      {c.cifVat || PLACEHOLDER}
                    </td>
                    <td>
                      <VatDot
                        vatNumber={c.vatNumber}
                        status={c.vatStatus}
                        title={vatTitle(c, t)}
                        placeholder={PLACEHOLDER}
                      />
                    </td>
                    <td className="meta">{c.billingAddress?.city || PLACEHOLDER}</td>
                    <td className="meta">
                      {c.billingAddress?.countryCode
                        ? `${countryFlag(c.billingAddress.countryCode)} ${countryName(c.billingAddress.countryCode, lang)}`
                        : PLACEHOLDER}
                    </td>
                    <td className="mono" style={{ fontSize: 12, color: 'var(--muted)' }}>
                      {c.emails[0] ?? PLACEHOLDER}
                    </td>
                    <td className="meta">
                      {c.ownerAgentId ? (
                        <div className="col">
                          <span style={{ color: 'var(--ink)' }}>
                            {c.ownerAgentName ?? t('unassigned')}
                          </span>
                          {c.ownerAgentEmail && (
                            <span className="mono" style={{ fontSize: 12, color: 'var(--muted)' }}>
                              {c.ownerAgentEmail}
                            </span>
                          )}
                        </div>
                      ) : (
                        t('unassigned')
                      )}
                    </td>
                    {MODULES.orders && (
                      <td className="mono" style={{ textAlign: 'right' }}>
                        {c.totals?.orderCount ?? 0}
                      </td>
                    )}
                    {MODULES.orders && <td className="meta">{lastOrderLabel(c, lang)}</td>}
                    <td className="mono" style={{ textAlign: 'right' }}>
                      {c.deliveryAddresses?.length ?? 0}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <div className="row gap-8 end">
                        <button
                          type="button"
                          className="btn sm"
                          onClick={() => navigate(`/admin/clients/${c.id}/edit`)}
                        >
                          {t('viewEditBtn')}
                        </button>
                        {canDelete && (
                          <button
                            type="button"
                            className="btn sm"
                            disabled={removeMutation.loading}
                            onClick={() => onDelete(c)}
                          >
                            {t('deleteBtn')}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
